import React, { Fragment, useCallback, useMemo } from "react";
import { Route, Routes, Navigate } from "react-router";
import { AlertProvider } from "./contexts/AlertContext";
import Start from "./components/Start";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Impressum from "./components/Impressum";
import Contact from "./components/Contact";
import HomePatient from "./components/componentsPatient/HomePatient";
import RunSurvey from "./components/componentsPatient/RunSurvey";
import PatientReport from "./components/componentsPatient/PatientReport";
import HomeClinician from "./components/componentsClinician/HomeClinician";
import HomeClinicAdmin from "./components/componentsClinicAdmin/HomeClinicAdmin";
import LoginPatient from "./components/componentsPatient/LoginPatient";
import LoginClinician from "./components/componentsClinician/LoginClinician";
import LoginClinicAdmin from "./components/componentsClinicAdmin/LoginClinicAdmin";
import Registration from "./components/componentsClinicAdmin/Registration";
import ClinicianReport from "./components/componentsClinician/ClinicianReport";
import InformationPagePatient from "./components/componentsPatient/InformationPagePatient";
import InformationPageClinician from "./components/componentsClinician/InformationPageClinician";
import InformationPageAdmin from "./components/componentsClinicAdmin/InformationPageAdmin";
import { useAuth } from "./contexts/AuthContext";
import HomePatientProvider from "./contexts/HomePatientContext";
import { useAlert } from "./contexts/AlertContext";
import { registerGlobalAlert } from "./helper/globalAlert";
import TimeoutSession from "./helper/TimeoutSession";
import { SessionProvider } from "./contexts/SessionContext";

export const documentTitle = window.APP_CONFIG.REACT_APP_REHACAT_DOCUMENT_TITLE;
const sysdokVersion = window.APP_CONFIG.REHACAT_APP_VERSION;

const Page404 = () => <h1> 404 Not Found </h1>;

// declare ProtectedRoute Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo: string;
  state?: { redirectTo: string; [key: string]: unknown };
}

const ProtectedRoute = ({
  children,
  redirectTo,
  state,
}: ProtectedRouteProps) => {
  const { state: authState } = useAuth();
  const { isAuthenticated } = authState;

  return isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to={redirectTo} state={state} replace />
  );
};

//log level configuration
const useLogLevelConfig = () => {
  return useMemo(() => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    const logLevel = window.APP_CONFIG?.REACT_APP_REHACAT_LOG_LEVEL || "info";

    switch (logLevel) {
      case "debug":
        break;
      case "info":
        console.debug = function () {
          /* empty */
        };
        console.log = function () {
          /* empty */
        };
        break;
      case "warning":
        console.debug = function () {
          /* empty */
        };
        console.log = function () {
          /* empty */
        };
        console.info = function () {
          /* empty */
        };
        break;
      case "error":
        console.log = function () {
          /* empty */
        };
        console.warn = function () {
          /* empty */
        };
        console.debug = function () {
          /* empty */
        };
        console.info = function () {
          /* empty */
        };
        break;
      default:
        console.debug = function () {
          /* empty */
        };
        console.log = function () {
          /* empty */
        };
        break;
    }
  }, []);
};

const useRouteConfig = () => {
  return useMemo(
    () => ({
      publicRoutes: [
        { path: "/", element: <Start title={documentTitle} /> },
        {
          path: "/loginpatient",
          element: <LoginPatient title={documentTitle + " | Login Patient"} />,
        },
        {
          path: "/loginclinician",
          element: (
            <LoginClinician title={`${documentTitle} | Login Mitarbeitende`} />
          ),
        },
        {
          path: "/loginadmin",
          element: (
            <LoginClinicAdmin title={`${documentTitle} | Login Admin`} />
          ),
        },
        {
          path: "/regist",
          element: <Registration title={`${documentTitle} | Registrierung`} />,
        },
        {
          path: "/privacypolicy",
          element: <PrivacyPolicy title={`${documentTitle} | Datenschutz`} />,
        },
        {
          path: "/impressum",
          element: <Impressum title={`${documentTitle} | Impressum`} />,
        },
        {
          path: "/contact",
          element: <Contact title={`${documentTitle} | Kontakt`} />,
        },
      ],

      protectedRoutes: [
        {
          path: "/patients",
          redirectTo: "/loginpatient",
          element: (
            <HomePatientProvider>
              <HomePatient title={`${documentTitle} | Startseite`} />
            </HomePatientProvider>
          ),
        },
        {
          path: "/patients/survey",
          redirectTo: "/loginpatient",
          state: { redirectTo: "/patients/survey" },
          element: <RunSurvey title={`${documentTitle} | Befragung`} />,
        },
        {
          path: "/patients/report",
          redirectTo: "/loginpatient",
          element: (
            <PatientReport title={`${documentTitle} | Patientenbericht`} />
          ),
        },
        {
          path: "/patients/information",
          redirectTo: "/loginpatient",
          element: (
            <InformationPagePatient
              title={`${documentTitle} | Informationen`}
            />
          ),
        },

        //clinician routes
        {
          path: "/clinician",
          redirectTo: "/loginclinician",
          element: <HomeClinician title={`${documentTitle} | Startseite`} />,
        },
        {
          path: "/clinician/clinician-report",
          redirectTo: "/loginclinician",
          element: (
            <ClinicianReport title={`${documentTitle} | Mitarbeiterbericht`} />
          ),
        },
        {
          path: "/clinician/patient-report",
          redirectTo: "/loginclinician",
          element: (
            <PatientReport title={`${documentTitle} | Patientenbericht`} />
          ),
        },
        {
          path: "/clinician/information",
          redirectTo: "/loginclinician",
          element: (
            <InformationPageClinician
              title={`${documentTitle} | Informationen`}
            />
          ),
        },

        //admin routes
        {
          path: "/admin",
          redirectTo: "/loginadmin",
          element: <HomeClinicAdmin title={`${documentTitle} | Startseite`} />,
        },
        {
          path: "/admin/information",
          redirectTo: "/loginadmin",
          element: (
            <InformationPageAdmin title={`${documentTitle} | Informationen`} />
          ),
        },
      ],
    }),
    [],
  );
};

const AppContent = () => {
  useLogLevelConfig();
  const alertContext = useAlert();

  React.useEffect(() => {
    registerGlobalAlert(alertContext);
    console.log("Global alert registered");
    console.log("Sysdok release version:", sysdokVersion);
  }, [alertContext]);

  const { publicRoutes, protectedRoutes } = useRouteConfig();

  const renderPublicRoutes = useCallback(
    () =>
      publicRoutes.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      )),
    [publicRoutes],
  );

  const renderProtectedRoutes = useCallback(
    () =>
      protectedRoutes.map(({ path, redirectTo, state, element }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute redirectTo={redirectTo} state={state}>
              {element}
            </ProtectedRoute>
          }
        />
      )),
    [protectedRoutes],
  );

  return (
    <Routes>
      {renderPublicRoutes()}
      {renderProtectedRoutes()}
      <Route path="*" element={<Page404 />} />
    </Routes>
  );
};

const App = () => {
  return (
    <Fragment>
      <SessionProvider>
        <AlertProvider>
          <TimeoutSession />
          <AppContent />
        </AlertProvider>
      </SessionProvider>
    </Fragment>
  );
};

export default App;
